import { Heart, Sparkles, Gift, TrendingUp, ArrowRight } from 'lucide-react';

import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Container from './Main/sections/Container';
import andy from '../assets/images/askAndy.png';

const REGISTER_URL = 'https://app.schoolhack.ai/auth/register';

const STATS = [
  { value: '3.5M+', label: 'Students Worldwide' },
  { value: '5', label: 'AI-Powered Tools' },
  { value: '100%', label: 'Currently Free' },
  { value: '50+', label: 'Partner Universities' },
];

const VALUES = [
  { icon: Heart, title: 'Students first', body: 'Every decision starts with one question: does this genuinely help someone learn better?' },
  { icon: Sparkles, title: 'AI with purpose', body: 'We use AI to remove busywork — not to replace real understanding. Andy teaches, never cheats for you.' },
  { icon: Gift, title: 'Free for everyone', body: 'Great study tools shouldn\'t be a paywall. Our core features are free, forever, for every student.' },
  { icon: TrendingUp, title: 'Always improving', body: 'We ship fast and listen closely. The product you use next month is shaped by students today.' },
];

const AboutUs = () => {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="We're On a Mission to"
        titleAccent="Make Learning Effortless"
        subtitle="School Hack started with a simple frustration — studying is harder than it should be. So we built the AI-powered study set we always wished we had."
      />

      {/* story */}
      <section className="relative py-14 lg:py-20">
        <Container className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <Reveal>
              <span className="eyebrow">Our Story</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-heading mt-5 text-[1.8rem] font-bold leading-[1.15] text-white sm:text-[2.3rem]">
                <span className="head-fade">Turning notes into </span>
                <span className="text-accent-grad">real understanding</span>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <div className="mt-5 flex flex-col gap-4 text-[0.98rem] leading-relaxed text-white/60">
                <p>
                  Students spend hours rewriting notes, making flashcards, and
                  hunting for answers — time that should be spent actually learning.
                  We knew AI could take that load off.
                </p>
                <p>
                  So we built School Hack: upload any material and instantly get
                  interactive notes, flashcards, quizzes, podcasts, and a personal AI
                  tutor named Andy who actually knows your content. Today, millions of
                  students study smarter with it — for free.
                </p>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-primary mt-8 px-6 py-3.5 text-[0.95rem]">
                Join them for free
                <ArrowRight size={17} />
              </a>
            </Reveal>
          </div>

          <Reveal delay={160} className="relative mx-auto w-full max-w-[360px]">
            <div className="glow left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 bg-accent/16" />
            <div className="card-dark relative rounded-3xl p-8 text-center">
              <img src={andy} alt="Andy" className="mx-auto h-28 w-28 animate-float object-contain drop-shadow-[0_10px_30px_rgba(94,216,178,0.4)]" />
              <p className="font-script mt-4 text-[1.6rem] text-accent-grad">Hi, I&apos;m Andy 👋</p>
              <p className="mt-2 text-[0.9rem] text-white/55">
                Your AI study partner, built into everything School Hack does.
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* stats */}
      <section className="relative py-10">
        <Container>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="glass glass-hover flex flex-col items-center gap-2 rounded-2xl px-4 py-7 text-center">
                  <div className="font-heading text-[2rem] font-bold text-white md:text-[2.4rem]">
                    {s.value}
                  </div>
                  <div className="text-[0.85rem] text-white/50">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* values */}
      <section className="relative py-14 lg:py-20">
        <div className="glow left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative">
          <Reveal className="text-center">
            <span className="eyebrow">What We Believe</span>
            <h2 className="font-heading mx-auto mt-5 max-w-2xl text-[2rem] font-bold leading-[1.1] text-white sm:text-[2.5rem]">
              <span className="head-fade">The Values That </span>
              <span className="text-accent-grad">Guide Us</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 80}>
                <div className="glass glass-hover h-full rounded-2xl p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/12 text-accent">
                    <v.icon size={21} />
                  </span>
                  <h3 className="font-heading mt-5 text-[1.1rem] font-semibold text-white">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-[0.87rem] leading-relaxed text-white/55">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
};

export default AboutUs;
